// src/modules/app-views/crm/seed.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import app from 'configs/appResolver';
import { THEME_COLOR } from 'constants/ThemeConstant';
import appService from 'services/appService';
import { generateUidV4 } from 'utils/helpers';
import translateX from 'utils/translateX';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
// App-specific seed overrides (fallback to buildDefaultSeeds when not defined)
const APP_SEEDS = {};

// *******************************************************************************
// MAIN FUNCTION
// *******************************************************************************
async function seedCrmParameters({ moduleId, storedParameters }) {
  if (!moduleId) return;

  const moduleParams = storedParameters?.modules?.[moduleId];

  // Skip seeding only if boards or boardGroups are already configured.
  // Other keys like boardOrders (saved by the kanban UI) must NOT prevent seeding.
  const hasBoards = moduleParams?.boards?.length > 0;
  const hasBoardGroups = moduleParams?.boardGroups?.length > 0;
  if (hasBoards || hasBoardGroups) return [];

  const appId = app?.ID;
  const seeds = APP_SEEDS[appId] || buildDefaultSeeds();
  const results = [];

  // Seed board groups
  const boardGroupsResult = await seedCategory({
    moduleId,
    category: 'boardGroups',
    data: seeds.boardGroups,
  });
  if (boardGroupsResult) results.push(boardGroupsResult);

  // Seed boards
  const boardsResult = await seedCategory({
    moduleId,
    category: 'boards',
    data: seeds.boards,
  });
  if (boardsResult) results.push(boardsResult);

  // Seed temperatures
  const temperaturesResult = await seedCategory({
    moduleId,
    category: 'temperatures',
    data: seeds.temperatures,
  });
  if (temperaturesResult) results.push(temperaturesResult);

  return results;
}

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default seedCrmParameters;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function getAppColor() {
  return THEME_COLOR[app?.THEME_COLOR] || '#1890ff';
}

export function buildDefaultSeeds() {
  const groupUid = generateUidV4();
  const boardUid = generateUidV4();

  return {
    boardGroups: [{ uid: groupUid, label: translateX('commercial') }],
    boards: [
      {
        uid: boardUid,
        title: translateX('sales'),
        boardGroupUid: groupUid,
        backColor: getAppColor(),
        description: '',
        fixedColumns: true,
        allowNewColumns: false,
        allowColumnEdit: false,
        allowMoveCards: false,
        columns: [
          { uid: generateUidV4(), title: translateX('leads'), order: 1, color: '#8c8c8c', initial: true, description: translateX('seed_col_leads') },
          { uid: generateUidV4(), title: translateX('in_contact'), order: 2, color: '#FFD300', description: translateX('seed_col_in_contact') },
          { uid: generateUidV4(), title: translateX('qualified'), order: 3, color: '#FF5F1F', description: translateX('seed_col_qualified') },
          { uid: generateUidV4(), title: translateX('meeting'), order: 4, color: '#FF00E5', description: translateX('seed_col_meeting') },
          { uid: generateUidV4(), title: translateX('proposal_sent'), order: 5, color: '#8A2BE2', description: translateX('seed_col_proposal_sent') },
          { uid: generateUidV4(), title: translateX('negotiation'), order: 6, color: '#39FF14', description: translateX('seed_col_negotiation') },
          { uid: generateUidV4(), title: translateX('finalized'), order: 7, color: '#595959', final: true, description: translateX('seed_col_finalized') },
        ],
      },
    ],
    temperatures: [
      { uid: generateUidV4(), label: 'cold', color: '#1890ff' },
      { uid: generateUidV4(), label: 'warm', color: '#faad14' },
      { uid: generateUidV4(), label: 'hot', color: '#ff4d4f' },
    ],
  };
}

async function seedCategory({ moduleId, category, data }) {
  try {
    await appService.updateModulePreferences({
      category,
      moduleId,
      data,
    });

    return { category, seeded: true, data };
  } catch (error) {
    console.error(`[seedCrmParameters] Error seeding ${category}:`, error);
    return { category, seeded: false, error };
  }
}
